import { useParams, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { getTranslation } from "../services/translations";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { categories, getInitials } from "./teamData";
import "./TeamDeptPage.css";

const deptIcons = {
  medical:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  clinical: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28"><path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"/></svg>,
  pharmacy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28"><path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
  nursing:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,
  admin:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
};

const TeamDeptPage = () => {
  const { deptKey } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = k => getTranslation(language, k);
  const cat = categories.find(c => c.key === deptKey);

  if (!cat) {
    return (
      <div className="tdp-page nb-page-offset">
        <Navbar />
        <div className="tdp-not-found">
          <h2>Department not found</h2>
          <button onClick={() => navigate("/team")}>Back to Team</button>
        </div>
      </div>
    );
  }

  return (
    <div className="tdp-page nb-page-offset">
      <Navbar />

      {/* Hero */}
      <section className="tdp-hero" style={{"--cat-color": cat.color}}>
        <div className="tdp-hero-overlay" />
        <div className="tdp-hero-content">
          <button className="tdp-back-btn" onClick={() => navigate("/team")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            {t("backToTeam")}
          </button>
          <div className="tdp-hero-eyebrow">{t("ourTeamEyebrow")}</div>
          <div className="tdp-hero-title-row">
            <div className="tdp-hero-icon" style={{background: cat.bg, color: cat.color}}>
              {deptIcons[cat.key]}
            </div>
            <h1 className="tdp-hero-title">{t(cat.titleKey)}</h1>
          </div>
          <div className="tdp-hero-breadcrumb">
            <Link to="/" className="tdp-breadcrumb-link">{t("home")}</Link>
            <span className="tdp-breadcrumb-sep">›</span>
            <Link to="/team" className="tdp-breadcrumb-link">{t("ourTeamBreadcrumb")}</Link>
            <span className="tdp-breadcrumb-sep">›</span>
            <span>{t(cat.titleKey)}</span>
          </div>
        </div>
      </section>

      {/* Members grid */}
      <section className="tdp-members-section">
        <div className="tdp-members-inner">
          <div className="tdp-members-header">
            <span className="tdp-members-count" style={{background: cat.color}}>
              {cat.members.length} {cat.members.length === 1 ? t("memberSingular") : t("memberPlural")}
            </span>
          </div>

          <div className="tdp-members-grid">
            {cat.members.map((m, i) => (
              <div
                key={i}
                className="tdp-member-card"
                style={{"--cat-color": cat.color, "--cat-bg": cat.bg, "--cat-border": cat.border}}
              >
                <div className="tdp-member-photo-wrapper">
                  <div className="tdp-member-photo-frame">
                    {m.image ? (
                      <img src={`${process.env.PUBLIC_URL}${m.image}`} alt={m.name} className="tdp-member-photo" onError={(e) => {e.target.style.display = 'none'}} />
                    ) : (
                      <div className="tdp-member-avatar-fallback" style={{background: cat.bg, color: cat.color}}>
                        {getInitials(m.name)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="tdp-member-info-section">
                  <div className="tdp-member-info-box">
                    <div className="tdp-member-name">{m.name}</div>
                    <div className="tdp-member-desig">{m.designation}</div>
                  </div>
                  <div className="tdp-member-footer">
                    <div className="tdp-member-dept-badge" style={{background: cat.color, color: "#fff"}}>
                      {t(cat.titleKey)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other departments */}
      <section className="tdp-other-section">
        <div className="tdp-other-inner">
          <h3 className="tdp-other-title">{t("otherDepartments")}</h3>
          <div className="tdp-other-links">
            {categories.filter(c => c.key !== cat.key).map(c => (
              <Link
                key={c.key}
                to={`/team/${c.key}`}
                className="tdp-other-link"
                style={{"--cat-color": c.color, "--cat-bg": c.bg}}
              >
                <span style={{color: c.color}}>{deptIcons[c.key]}</span>
                <span>{t(c.titleKey)}</span>
                <span className="tdp-other-count" style={{background: c.color}}>{c.members.length}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default TeamDeptPage;
